import React, {useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle, useCallback} from 'react'
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
    AdditionalActions,
    BottomControlsContainer,
    CenterControls,
    Divider,
    LeftControls,
    MainControlsContainer,
    RightControls,
    TimeLineWrapper,
    VideoControlsWrapper,
    VolumeTooltip,
    VolumeControl,
    SpeedWrapper
} from './VideoControls';
import {$apiV1} from '../../../../config/api';
import { getUserProfile, invalidateUserProfile } from '../../../../helpers/userProfile';

// icons
import {ReactComponent as PlayIcon} from './icons/ic_play.svg';
import {ReactComponent as PauseIcon} from './icons/pause.svg';
import {ReactComponent as Minus5Icon} from './icons/ic_-5.svg';
import {ReactComponent as Plus5Icon} from './icons/ic_+5.svg';
import {ReactComponent as PrevIcon} from './icons/ic_prev.svg';
import {ReactComponent as NextIcon} from './icons/ic_next.svg';
import {ReactComponent as SettingsIcon} from './icons/ic_settings2.svg';
import {ReactComponent as FullScreenIcon} from './icons/fullscreen-line.svg';
import {ReactComponent as ScreenShotIcon} from './icons/ic_capture.svg';
import {ReactComponent as RecordIcon} from './icons/record-button.svg';
import {ReactComponent as MutedIcon} from './icons/ic_mute.svg';
import {ReactComponent as VolumeIcon} from './icons/ic_volume.svg';
import {IconWrapper} from './icons/IconWrapper';
import TextInput from '../../../../components/inputs/TextInput';
import {Input} from './Input';
import {Oval} from 'react-loader-spinner';
import VideoDownloader from './VideoDownloader';
import {downloadVideosActions} from '../../../../store/download_videos/downloadVideosSlice';
import {useDispatch, useSelector} from 'react-redux';
import {useNotification} from '../../../../components/notification/useNotification';
import {NotificationList} from '../../../../components/notification/NotificationList';
import {userActions} from '../../../../store/user/userSlice';
import {useLexicon} from '../../../../hooks/useLexicon';
import {API_URL_V2} from '../../../../config/api';
import { trackVideoAction } from '../../../../tracking';
import { generateTrackingId, getSourceSectionFromPath } from '../../../../tracking/utils';

const SEEK_DETECTION_THRESHOLD_SECONDS = 1.5;

// Entrance for both the chip and the collapsed handle: a soft fade + rise.
const chipReveal = keyframes`
    from { opacity: 0; transform: translateY(8px) scale(0.94); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// Gentle attention pulse on the collapsed handle so it reads as "there's an
// option here". Runs a few times after it appears, then settles.
const handlePulse = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(198, 240, 0, 0.5); }
    70%  { box-shadow: 0 0 0 9px rgba(198, 240, 0, 0); }
    100% { box-shadow: 0 0 0 0 rgba(198, 240, 0, 0); }
`;

// Contextual overlay shown in the video's bottom-right corner when the current
// event belongs to a possession sequence. The main action toggles between
// playing the whole sequence and just the single event; the × collapses it to
// a small handle so it stops covering the picture.
const FullSequenceChip = styled.div`
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 999px;
    background: rgba(4, 8, 2, 0.7);
    backdrop-filter: blur(4px);
    font-family: 'SatoshiMedium';
    font-size: 13px;
    line-height: 1;
    transform-origin: bottom right;
    animation: ${chipReveal} 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;

    .chip-action {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 10px 7px 12px;
        background: transparent;
        border: none;
        cursor: pointer;
        color: ${(props) => props.theme.green};
        border-radius: 999px 0 0 999px;
        transition: background 0.2s ease, color 0.2s ease;
    }

    .chip-action:hover {
        background: ${(props) => props.theme.green};
        color: ${(props) => props.theme.black};
    }

    .chip-action > svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        transition: transform 0.2s ease;
    }

    .chip-action:hover > svg {
        transform: scale(1.12);
    }

    .chip-action.mid {
        border-radius: 0;
    }

    .chip-nav {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 10px;
        background: transparent;
        border: none;
        cursor: pointer;
        color: ${(props) => props.theme.green};
        transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease;
    }

    .chip-nav:hover {
        background: ${(props) => props.theme.green};
        color: ${(props) => props.theme.black};
    }

    .chip-nav:disabled {
        opacity: 0.35;
        cursor: default;
        background: transparent;
        color: ${(props) => props.theme.green};
    }

    .chip-nav.prev {
        border-radius: 999px 0 0 999px;
        padding-left: 12px;
    }

    .chip-divider {
        width: 1px;
        align-self: stretch;
        margin: 5px 0;
        background: rgba(255, 255, 255, 0.15);
        flex-shrink: 0;
    }

    .chip-close {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin: 0 4px 0 1px;
        padding: 0;
        background: transparent;
        border: none;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.6);
        font-size: 15px;
        border-radius: 50%;
        transition: background 0.2s ease, color 0.2s ease;
    }

    .chip-close:hover {
        background: rgba(255, 255, 255, 0.12);
        color: #fff;
    }

    .chip-hide-label {
        position: absolute;
        bottom: calc(100% + 6px);
        right: 0;
        padding: 3px 6px;
        border-radius: 4px;
        background: rgba(4, 8, 2, 0.92);
        color: #fff;
        font-size: 11px;
        white-space: nowrap;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;
    }

    .chip-close:hover .chip-hide-label {
        opacity: 1;
    }
`;

// Collapsed state: a small round handle that hints a hidden option is there.
const SequenceChipHandle = styled.button`
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    background: rgba(4, 8, 2, 0.7);
    backdrop-filter: blur(4px);
    color: ${(props) => props.theme.green};
    cursor: pointer;
    transform-origin: bottom right;
    animation: ${chipReveal} 0.28s cubic-bezier(0.22, 1, 0.36, 1) both,
        ${handlePulse} 2.4s ease-out 0.6s 3;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

    &:hover {
        background: ${(props) => props.theme.green};
        color: ${(props) => props.theme.black};
        transform: scale(1.08);
    }

    & > svg {
        width: 17px;
        height: 17px;
    }
`;

// Video camera: used for both the full-sequence and single-event states.
const CameraIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="3" y="7" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M15 10.5l5.4-2.7a.6.6 0 0 1 .87.54v7.32a.6.6 0 0 1-.87.54L15 13.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);

const getRouteTrackingMetadata = (pathname) => {
    const playlistMatch = String(pathname || '').match(/^\/my-playlists\/(\d+)/);
    const sharedPlaylistMatch = String(pathname || '').match(/^\/shared_playlist\/(\d+)/);

    return {
        playlist_id: playlistMatch ? Number(playlistMatch[1]) : null,
        shared_playlist_id: sharedPlaylistMatch ? Number(sharedPlaylistMatch[1]) : null,
    };
};

const ChangeSpeed = ({speed, onSpeedChange}) => {
    const speedLevel = useMemo(() => {
        return `${speed}x`
    }, [speed])


    return (
        <VolumeControl>
            <SpeedWrapper>
                <span>{speedLevel}</span>
            </SpeedWrapper>
            <VolumeTooltip>
                <input
                    type="range"
                    id="volume-slider"
                    min={0.25}
                    max={4}
                    step='0.25'
                    value={speed}
                    onInput={(e) => onSpeedChange(e.target.value)}
                />
                <span id="volume-level">{speedLevel}</span>
            </VolumeTooltip>
        </VolumeControl>
    )
}

const ChangeVolume = ({volume, onVolumeChange}) => {
    const volumeLevel = useMemo(() => {
        return `${(+volume * 100).toFixed(0)}%`
    }, [volume])

    return (
        <VolumeControl>
            <IconWrapper
                //  onClick={() => onVolumeChange(volume === 0 ? 1 : 0)} // need fix for mobile
            >
                {volume !== 0 ? (
                    <VolumeIcon width={23} height={20}/>
                ) : (
                    <MutedIcon width={20} height={20}/>
                )}
            </IconWrapper>
            <VolumeTooltip>
                <input
                    type="range"
                    id="volume-slider"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onInput={(e) => onVolumeChange(e.target.value)}
                />
                <span id="volume-level">{volumeLevel}</span>
            </VolumeTooltip>
        </VolumeControl>
    )
}

const VideoSettings = (props) => {
    const {
        changedOffsets
    } = props;
    const l = useLexicon();
    const dispatch = useDispatch();
    // Actual saved values
    const [offset_range_begin, setOffset_range_begin] = useState(10);
    const [offset_range_end, setOffset_range_end] = useState(10);
    const [merge_offset, setMerge_offset] = useState(10);
    // Local input values (allow intermediate states like empty string)
    const [inputBegin, setInputBegin] = useState('10');
    const [inputEnd, setInputEnd] = useState('10');
    const [inputMerge, setInputMerge] = useState('10');

    useEffect(() => {
        getOffsets();
    }, []);

    const saveOffset = (type, value) => {
        const numValue = +value;
        if (type === 'begin' && numValue > 0) {
            $apiV1.patch(`/users/profile_settings`, {
                offset_range_begin: numValue,
            }).then(async () => {
                setOffset_range_begin(numValue);
                await dispatch(userActions.changeOffsetBegin(numValue));
                invalidateUserProfile();
            }).catch(console.log);
        } else if (type === 'end' && numValue > 0) {
            $apiV1.patch(`/users/profile_settings`, {
                offset_range_end: numValue,
            }).then(async () => {
                setOffset_range_end(numValue);
                await dispatch(userActions.changeOffsetEnd(numValue));
                invalidateUserProfile();
            }).catch(console.log);
        } else if (type === 'merge' && numValue >= 0 && numValue <= 15) {
            $apiV1.patch(`/users/profile_settings`, {
                merge_offset: numValue,
            }).then(async () => {
                setMerge_offset(numValue);
                await dispatch(userActions.changeMergeOffset(numValue));
                invalidateUserProfile();
            }).catch(console.log);
        }
    }

    const getOffsets = () => {
        getUserProfile().then((res) => {
            setOffset_range_begin(res.data.offset_range_begin);
            setOffset_range_end(res.data.offset_range_end);
            setMerge_offset(res.data.merge_offset);
            // Sync input values
            setInputBegin(String(res.data.offset_range_begin ?? ''));
            setInputEnd(String(res.data.offset_range_end ?? ''));
            setInputMerge(String(res.data.merge_offset ?? ''));

            console.log("GOT OFFSETS:   ", res.data);
        })
    }

    const handleBlurBegin = () => {
        const numValue = +inputBegin;
        if (numValue > 0) {
            saveOffset('begin', numValue);
        } else {
            // Revert to last valid value
            setInputBegin(String(offset_range_begin));
        }
    }

    const handleBlurEnd = () => {
        const numValue = +inputEnd;
        if (numValue > 0) {
            saveOffset('end', numValue);
        } else {
            // Revert to last valid value
            setInputEnd(String(offset_range_end));
        }
    }

    const handleBlurMerge = () => {
        let numValue = +inputMerge;
        if (numValue < 0) {
            numValue = 0;
        } else if (numValue > 15) {
            numValue = 15;
        }
        if (!isNaN(numValue) && inputMerge !== '') {
            saveOffset('merge', numValue);
            setInputMerge(String(numValue));
        } else {
            // Revert to last valid value
            setInputMerge(String(merge_offset));
        }
    }

    return (
        <VolumeControl>
            <IconWrapper>
                <SettingsIcon/>
            </IconWrapper>
            <VolumeTooltip style={{top: '-70px', left: '-150px', zIndex: 1000}}>
                <Input
                    value={inputBegin}
                    type='number'
                    min={1}
                    placeholder={l(1030)}
                    label={l(1030)}
                    style={{background: '#000000', color: 'white', zIndex: 1000}}
                    onChange={(e) => setInputBegin(e.target.value)}
                    onBlur={handleBlurBegin}
                />
                <Input
                    value={inputEnd}
                    type='number'
                    min={1}
                    placeholder={l(1031)}
                    label={l(1031)}
                    style={{background: '#000000', color: 'white', zIndex: 1000}}
                    onChange={(e) => setInputEnd(e.target.value)}
                    onBlur={handleBlurEnd}
                />
                <Input
                    value={inputMerge}
                    type='number'
                    min={0}
                    max={15}
                    placeholder={l(1112)}
                    label={l(1112)}
                    style={{background: '#000000', color: 'white', zIndex: 1000}}
                    onChange={(e) => setInputMerge(e.target.value)}
                    onBlur={handleBlurMerge}
                />
            </VolumeTooltip>
        </VolumeControl>
    )
}

export const VideoPlayer = forwardRef((props, ref) => {
    const {
        video: videoLink,
        // offsets,
        // changeOffSets,
        videos,
        loading,
        addRecordToPlaylist,
        recordTime,
        setRecordTime,
        playNextVideo,
        playPrevVideo,
        disabledNext,
        disabledPrev,
        needDownload = false,
        styleWrapper = {},
        addToPlaylist,
        onVideoDurationChange,
        showAddToPlaylistButton = false,
        onExtendVideo,
        isLoadingRef,
        toSecond,
        record,
        stacked = false,
        changedOffsets,
        onShowFullSequence,
        onShowEventOnly,
        onShowPreviousSequence,
        onShowNextSequence,
        hasPreviousSequence = false,
        hasNextSequence = false,
        isFullSequenceActive = false

    } = props;
    const l = useLexicon();
    const location = useLocation();
    const dispatch = useDispatch();
    const [skipTime, setSkipTime] = useState(5);
    const [sequenceChipHidden, setSequenceChipHidden] = useState(false);


    const {addNotification, removeNotification, notifications} = useNotification();

    const videoRef = useRef(null);
    const videoContainer = useRef(null);
    const playPauseBtn = useRef(null);
    const totalTimeElem = useRef(null);
    const currentTimeElem = useRef(null);
    const timelineContainer = useRef(null);
    const controlsRef = useRef(null);
    const pausedOverlay = useRef(null);
    const suppressAutoNextRef = useRef(false);
    const trackingStateRef = useRef({
        bufferedWatchedSeconds: 0,
        clipSessionId: generateTrackingId(),
        context: null,
        hasStarted: false,
        isPlaying: false,
        lastKnownTime: 0,
        lastPlaybackPosition: 0,
        totalWatchedSeconds: 0,
    });

    const wasPaused = useRef(true);
    const isScrubbing = useRef(false);

    const [nextVideo, setNextVideo] = useState(null);
    const [indexNext, setIndexNext] = useState(0);
    const [indexCurrent, setIndexCurrent] = useState(0);

    const [playbackRate, setPlaybackRate] = useState(localStorage.getItem('playbackRate') ?? 1);
    const [volume, setVolume] = useState(0);

    const [paused, setPaused] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const startMarker = useRef(null);
    const endMarker = useRef(null);
    // const [isDraggingStart, setIsDraggingStart] = useState(false);
    // const [isDraggingEnd, setIsDraggingEnd] = useState(false);

    const isAuth = localStorage.getItem('token');

    const getCurrentVideoEntry = useCallback(() => {
        if (record && typeof record === 'object') {
            return record;
        }

        return videos?.[indexCurrent] || null;
    }, [indexCurrent, record, videos]);

    const buildVideoTrackingContext = useCallback(() => {
        const currentVideo = getCurrentVideoEntry();
        const routeMetadata = getRouteTrackingMetadata(location.pathname);
        const sourceSurface = getSourceSectionFromPath(location.pathname) || 'video';

        return {
            entityId: currentVideo?.id || null,
            entityLabel: currentVideo?.title || null,
            entityType: 'video',
            metadata: {
                clip_end_second: currentVideo?.end_second ?? null,
                clip_start_second: currentVideo?.start_second ?? null,
                current_index: indexCurrent,
                playback_rate: Number(playbackRate) || 1,
                video_id: currentVideo?.id || null,
                ...routeMetadata,
            },
            sourceSurface,
        };
    }, [getCurrentVideoEntry, indexCurrent, location.pathname, playbackRate]);

    const resetTrackingState = useCallback((currentTime = 0) => {
        trackingStateRef.current = {
            bufferedWatchedSeconds: 0,
            clipSessionId: generateTrackingId(),
            context: buildVideoTrackingContext(),
            hasStarted: false,
            isPlaying: false,
            lastKnownTime: currentTime,
            lastPlaybackPosition: currentTime,
            totalWatchedSeconds: 0,
        };
    }, [buildVideoTrackingContext]);

    const flushTrackedWatchTime = useCallback((action, extraMetadata = {}) => {
        const trackingState = trackingStateRef.current;

        if (!trackingState.context || !trackingState.hasStarted) {
            return;
        }

        const watchedSecondsDelta = Number(trackingState.bufferedWatchedSeconds.toFixed(3));
        const watchedSecondsTotal = Number(trackingState.totalWatchedSeconds.toFixed(3));

        if (
            watchedSecondsDelta <= 0 &&
            watchedSecondsTotal <= 0 &&
            action !== 'pause' &&
            action !== 'complete'
        ) {
            return;
        }

        trackVideoAction({
            action,
            entityId: trackingState.context.entityId,
            entityLabel: trackingState.context.entityLabel,
            entityType: trackingState.context.entityType,
            metadata: {
                ...trackingState.context.metadata,
                clip_session_id: trackingState.clipSessionId,
                current_time: Number(Number(videoRef.current?.currentTime || trackingState.lastKnownTime || 0).toFixed(3)),
                watched_seconds_delta: watchedSecondsDelta,
                watched_seconds_total: watchedSecondsTotal,
                ...extraMetadata,
            },
            sourceSurface: trackingState.context.sourceSurface,
        });

        trackingState.bufferedWatchedSeconds = 0;
    }, []);

    const updateTrackedWatchTime = useCallback((currentTime) => {
        const trackingState = trackingStateRef.current;

        if (!trackingState.isPlaying) {
            trackingState.lastKnownTime = currentTime;
            trackingState.lastPlaybackPosition = currentTime;
            return;
        }

        const delta = currentTime - trackingState.lastPlaybackPosition;

        if (delta >= 0 && delta <= SEEK_DETECTION_THRESHOLD_SECONDS) {
            trackingState.bufferedWatchedSeconds += delta;
            trackingState.totalWatchedSeconds += delta;
        } else if (Math.abs(delta) > SEEK_DETECTION_THRESHOLD_SECONDS && trackingState.hasStarted) {
            trackVideoAction({
                action: 'seek',
                entityId: trackingState.context?.entityId || null,
                entityLabel: trackingState.context?.entityLabel || null,
                entityType: trackingState.context?.entityType || 'video',
                metadata: {
                    ...(trackingState.context?.metadata || {}),
                    clip_session_id: trackingState.clipSessionId,
                    from_time: Number(trackingState.lastPlaybackPosition.toFixed(3)),
                    seek_delta_seconds: Number(delta.toFixed(3)),
                    to_time: Number(currentTime.toFixed(3)),
                },
                sourceSurface: trackingState.context?.sourceSurface || 'video',
            });
        }

        trackingState.lastPlaybackPosition = currentTime;
        trackingState.lastKnownTime = currentTime;
    }, []);

    const handleTrackingPlayStart = useCallback(() => {
        const trackingState = trackingStateRef.current;
        const currentTime = Number(videoRef.current?.currentTime || 0);

        trackingState.context = trackingState.context || buildVideoTrackingContext();
        trackingState.isPlaying = true;
        trackingState.lastKnownTime = currentTime;
        trackingState.lastPlaybackPosition = currentTime;

        if (!trackingState.hasStarted) {
            trackingState.hasStarted = true;
            trackVideoAction({
                action: 'play',
                entityId: trackingState.context?.entityId || null,
                entityLabel: trackingState.context?.entityLabel || null,
                entityType: trackingState.context?.entityType || 'video',
                metadata: {
                    ...(trackingState.context?.metadata || {}),
                    clip_session_id: trackingState.clipSessionId,
                    current_time: Number(currentTime.toFixed(3)),
                },
                sourceSurface: trackingState.context?.sourceSurface || 'video',
            });
        }
    }, [buildVideoTrackingContext]);

    const handleTrackingPause = useCallback((flushReason = 'pause') => {
        const trackingState = trackingStateRef.current;
        const currentTime = Number(videoRef.current?.currentTime || trackingState.lastKnownTime || 0);
        const duration = Number(videoRef.current?.duration || 0);

        if (isScrubbing.current) {
            trackingState.isPlaying = false;
            trackingState.lastKnownTime = currentTime;
            trackingState.lastPlaybackPosition = currentTime;
            return;
        }

        trackingState.isPlaying = false;
        updateTrackedWatchTime(currentTime);

        if (duration > 0 && currentTime >= duration - 0.25) {
            return;
        }

        flushTrackedWatchTime('pause', {
            flush_reason: flushReason,
        });
    }, [flushTrackedWatchTime, updateTrackedWatchTime]);

    const handleTrackingComplete = useCallback(() => {
        const trackingState = trackingStateRef.current;
        const currentTime = Number(videoRef.current?.currentTime || trackingState.lastKnownTime || 0);

        trackingState.isPlaying = false;
        updateTrackedWatchTime(currentTime);
        flushTrackedWatchTime('complete', {
            flush_reason: 'ended',
        });
    }, [flushTrackedWatchTime, updateTrackedWatchTime]);


    // Define event handlers outside useEffect to maintain consistent references
    const handleLoadedData = useCallback(() => {
        flushTrackedWatchTime('pause', { flush_reason: 'clip_change' });
        resetTrackingState(typeof toSecond === 'number' && isFinite(toSecond) ? toSecond : 0);

        if (totalTimeElem?.current) {
            totalTimeElem.current.textContent = formatDuration(
                videoRef.current?.duration || 0,
            );
        }

        // Pass duration back to parent component
        if (onVideoDurationChange && videoRef.current?.duration) {
            onVideoDurationChange(videoRef.current.duration);
        }

        // new video loaded, re-enable auto-next
        suppressAutoNextRef.current = false;
        // if requested, seek to provided second within the cut
        if (videoRef.current && typeof toSecond === 'number' && isFinite(toSecond)) {
            const duration = Number.isFinite(videoRef.current.duration) ? videoRef.current.duration : 0;
            const clamped = Math.max(0, Math.min(duration > 0 ? duration - 0.05 : 0, toSecond));
            try {
                videoRef.current.currentTime = clamped;
            } catch (e) {
                // ignore seek errors
            }
        }
        setPaused(true);
        videoRef.current?.play();
    }, [flushTrackedWatchTime, onVideoDurationChange, resetTrackingState, toSecond]);

    const handleVideoTimeUpdate = useCallback(() => {
        const currentPlaybackTime = Number(videoRef.current?.currentTime || 0);

        if (currentTimeElem.current && timelineContainer.current) {
            currentTimeElem.current.textContent = `${formatDuration(
                videoRef.current?.currentTime || 0,
            )} / ${formatDuration(
                videoRef.current?.duration || 0,
            )}`;
            const percent = videoRef.current
                ? // eslint-disable-next-line no-unsafe-optional-chaining
                videoRef.current?.currentTime / videoRef.current?.duration
                : 0;
            timelineContainer.current.style.setProperty(
                '--progress-position',
                percent.toString(),
            );
        }
        updateTrackedWatchTime(currentPlaybackTime);
    }, [updateTrackedWatchTime]);

    const handleAddClassToVideoContainer = useCallback(() => {
        videoContainer.current?.classList.add('paused');
        pausedOverlay.current?.classList.add('active');
    }, []);

    const handleRemoveClassToVideoContainer = useCallback(() => {
        videoContainer.current?.classList.remove('paused');
        pausedOverlay.current?.classList.remove('active');
    }, []);

    const handleMouseUpFromTimeLine = useCallback((e) => {
        if (isScrubbing.current) {
            const rect = timelineContainer.current?.getBoundingClientRect();
            if (rect && videoRef.current) {
                const percent =
                    Math.min(Math.max(0, e.x - rect.x), rect.width) / rect.width;
                isScrubbing.current = false;
                videoRef.current.currentTime = percent * videoRef.current.duration;
                if (!wasPaused.current) videoRef.current.play();
            }
            // Handle timeline update inline to avoid reference issues
            if (timelineContainer.current && videoRef.current) {
                const rect = timelineContainer.current.getBoundingClientRect();
                const percent =
                    Math.min(Math.max(0, e.x - rect.x), rect.width) / rect.width;

                if (isScrubbing) {
                    e.preventDefault();
                    timelineContainer.current.style.setProperty(
                        '--progress-position',
                        percent.toString(),
                    );
                }
            }
        }
    }, []);

    const handleFullscreenChange = useCallback(() => {
        setFullScreen(Boolean(document?.fullscreenElement))
        videoContainer?.current?.classList.toggle(
            'full-screen',
            document.fullscreenElement,
        );
    }, []);

    // Define all functions used in useEffect before the useEffect
    const toggleFullScreenMode = useCallback(() => {
        const fullscreenElement = document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement;

        if (fullscreenElement) {
            exitFullscreen();
        } else {
            launchIntoFullscreen(videoContainer.current);
        }
    }, []);

    const togglePlay = useCallback(() => {
        wasPaused.current = !videoRef.current?.paused;
        videoRef.current?.paused
            ? videoRef.current?.play()
            : videoRef.current?.pause();
        setPaused(!videoRef.current?.paused);
    }, []);

    const onVolumeChange = useCallback((e) => {
        setVolume(+videoRef.current?.volume);
    }, []);

    const onSpeedChange = useCallback(() => {
        const storageSpeed = localStorage.getItem('playbackRate') ?? 1;
        const videoPlayerSpeed = +videoRef.current?.playbackRate ?? 1;
        if (videoPlayerSpeed > 1 || videoPlayerSpeed < 1) {
            videoRef.current.playbackRate = videoPlayerSpeed;
            setPlaybackRate(videoPlayerSpeed)
            return
        } else {
            videoRef.current.playbackRate = +storageSpeed;
            setPlaybackRate(+storageSpeed)
        }
    }, []);

    const toggleScrubbing = useCallback((e) => {
        const rect = timelineContainer.current?.getBoundingClientRect();
        if (rect && videoRef.current) {
            const percent =
                Math.min(Math.max(0, e.x - rect.x), rect.width) / rect.width;
            // eslint-disable-next-line no-bitwise
            isScrubbing.current = (e.buttons & 1) === 1;
            // videoContainer.current.classList.toggle('scrubbing', isScrubbing.current);
            if (isScrubbing.current) {
                wasPaused.current = videoRef.current?.paused;
                videoRef.current?.pause();
            } else {
                videoRef.current.currentTime = percent * videoRef.current.duration;
                if (!wasPaused.current) videoRef.current.play();
            }
        }

        // Handle timeline update inline to avoid reference issues
        if (timelineContainer.current && videoRef.current) {
            const rect = timelineContainer.current.getBoundingClientRect();
            const percent =
                Math.min(Math.max(0, e.x - rect.x), rect.width) / rect.width;

            if (isScrubbing) {
                e.preventDefault();
                timelineContainer.current.style.setProperty(
                    '--progress-position',
                    percent.toString(),
                );
            }
        }
    }, []);

    const handleKeyDown = useCallback((e) => {
        const tagName = document?.activeElement?.tagName.toLowerCase();

        if (tagName === 'input') return;
        switch (e.key.toLowerCase()) {
            case ' ':
                if (tagName === 'button') return;
                togglePlay();
                break;
            case 'f':
                toggleFullScreenMode();
                break;
            case 'arrowleft':
            case 'j':
                handleChangeSecond(0 - skipTime);
                break;
            case 'arrowright':
            case 'l':
                handleChangeSecond(Number(skipTime));
                break;
            default:
                break;
        }
    }, [togglePlay, toggleFullScreenMode, skipTime]);

    useEffect(() => {
        playPauseBtn.current?.addEventListener('click', togglePlay);

        videoRef.current?.addEventListener('click', togglePlay);
        videoRef.current?.addEventListener('loadeddata', handleLoadedData);
        videoRef.current?.addEventListener('timeupdate', handleVideoTimeUpdate);
        videoRef.current?.addEventListener(
            'play',
            handleRemoveClassToVideoContainer,
        );
        videoRef.current?.addEventListener('play', handleTrackingPlayStart);
        videoRef.current?.addEventListener('pause', handleAddClassToVideoContainer);
        videoRef.current?.addEventListener('pause', handleTrackingPause);
        videoRef.current?.addEventListener('ended', handleTrackingComplete);
        videoRef.current?.addEventListener('volumechange', onVolumeChange);
        videoRef.current?.addEventListener('ratechange', onSpeedChange);

        document.addEventListener('keydown', handleKeyDown);

        timelineContainer.current?.addEventListener('mousedown', toggleScrubbing);
        document.addEventListener('mouseup', handleMouseUpFromTimeLine);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        onSpeedChange();

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            playPauseBtn.current?.removeEventListener('click', togglePlay);
            videoRef.current?.removeEventListener('click', togglePlay);
            videoRef.current?.removeEventListener('loadeddata', handleLoadedData);
            videoRef.current?.removeEventListener(
                'timeupdate',
                handleVideoTimeUpdate,
            );
            videoRef.current?.removeEventListener(
                'play',
                handleRemoveClassToVideoContainer,
            );
            videoRef.current?.removeEventListener('play', handleTrackingPlayStart);
            videoRef.current?.removeEventListener(
                'pause',
                handleAddClassToVideoContainer,
            );
            videoRef.current?.removeEventListener('pause', handleTrackingPause);
            videoRef.current?.removeEventListener('ended', handleTrackingComplete);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            videoRef.current?.removeEventListener('volumechange', onVolumeChange);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            videoRef.current?.removeEventListener('ratechange', onSpeedChange);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            timelineContainer.current?.removeEventListener(
                'mousedown',
                toggleScrubbing,
            );

            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mouseup', handleMouseUpFromTimeLine);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleLoadedData, handleVideoTimeUpdate, handleAddClassToVideoContainer, handleRemoveClassToVideoContainer, handleMouseUpFromTimeLine, handleFullscreenChange, handleKeyDown, togglePlay, onVolumeChange, onSpeedChange, toggleScrubbing, toggleFullScreenMode, handleTrackingComplete, handleTrackingPause, handleTrackingPlayStart]);

    useEffect(() => {
        const handlePageExit = () => {
            handleTrackingPause('pagehide');
        };

        window.addEventListener('pagehide', handlePageExit);
        window.addEventListener('beforeunload', handlePageExit);

        return () => {
            window.removeEventListener('pagehide', handlePageExit);
            window.removeEventListener('beforeunload', handlePageExit);
        };
    }, [handleTrackingPause]);

    function fetchFile(url) {
        const tempUrl = url;
        const aTag = document.createElement('a');
        aTag.href = tempUrl;
        // eslint-disable-next-line
        aTag.download = url.replace(/^.*[\\\/]/, '');
        document.body.appendChild(aTag);
        aTag.click();
        URL.revokeObjectURL(tempUrl);
        aTag.remove();
    }

    const formatDuration = (time) => {
        const leadingZeroFormatter = new Intl.NumberFormat(undefined, {
            minimumIntegerDigits: 2,
        });
        const seconds = Math.floor(time % 60);
        const minutes = Math.floor(time / 60) % 60;
        const hours = Math.floor(time / 3600);
        if (hours === 0) {
            return `${minutes}:${leadingZeroFormatter.format(seconds)}`;
        }
        return `${hours}:${leadingZeroFormatter.format(
            minutes,
        )}:${leadingZeroFormatter.format(seconds)}`;
    };

    function skip(duration) {
        if (videoRef.current) {
            videoRef.current.currentTime += duration;
        }
    }

    function launchIntoFullscreen(element) {
        if (element.requestFullscreen) {
            element.requestFullscreen();
        } else if (element.mozRequestFullScreen) {
            element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) {
            element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
            element.msRequestFullscreen();
        } else {
            element.classList.toggle('fullscreen');
        }
    }

    function exitFullscreen() {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }

    const handleChangeSpeed = (value) => {
        if (videoRef.current) {
            videoRef.current.playbackRate = +value;
            localStorage.setItem('playbackRate', +value)
        }
    };


    const handleChangeVolume = (value) => {
        if (videoRef.current) {
            videoRef.current.volume = +value;
            videoRef.current.muted = +value === 0;
        }
    };

    const handleScreenshot = () => {
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current?.videoWidth;
        canvas.height = videoRef.current?.videoHeight;

        if (videoRef.current) {
            const context = canvas.getContext('2d');
            context.drawImage(
                videoRef.current,
                0,
                0,
                videoRef.current?.videoWidth,
                videoRef.current?.videoHeight,
            );
        }
        const dataURL = canvas.toDataURL('image/jpeg');

        const a = document.createElement('a');
        a.href = dataURL;
        a.download = `Screenshot_${videoRef.current?.currentTime}_match_${videos?.[indexCurrent]?.title}.jpeg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        const trackingContext = trackingStateRef.current.context || buildVideoTrackingContext();
        trackVideoAction({
            action: 'screenshot',
            entityId: trackingContext?.entityId || null,
            entityLabel: trackingContext?.entityLabel || null,
            entityType: trackingContext?.entityType || 'video',
            metadata: {
                ...(trackingContext?.metadata || {}),
                clip_session_id: trackingStateRef.current.clipSessionId,
                current_time: Number((videoRef.current?.currentTime || 0).toFixed(3)),
            },
            sourceSurface: trackingContext?.sourceSurface || 'video',
        });
    };

    const handleChangeSecond = (second) => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const delta = Number(second) || 0;
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const targetTime = video.currentTime + delta;

        // Reached or passed the end
        if (delta > 0 && duration && targetTime >= duration) {
            console.log("REACHED OR PASSED THE END");
            // prevent immediate auto-next triggered by onEnded of the current clip
            suppressAutoNextRef.current = true;
            if (typeof onExtendVideo === 'function') {
                const overflow = Math.ceil(targetTime - duration) || Math.ceil(delta);
                onExtendVideo('end', overflow, record);
                return;
            }
        }

        // Reached or before the beginning
        if (delta < 0 && targetTime <= 0) {
            console.log("REACHED OR BEFORE THE BEGINNING");
            // prevent immediate auto-prev triggered by onEnded/start boundary
            suppressAutoNextRef.current = true;
            if (typeof onExtendVideo === 'function') {
                const overflow = Math.ceil(Math.abs(targetTime)) || Math.ceil(Math.abs(delta));
                console.log("START EXTEND VIDEO", overflow, record);
                onExtendVideo('start', overflow, record);
                return;
            }
        }

        video.currentTime = targetTime;
    };

    const handleRecord = async (second) => {
        const trackingContext = trackingStateRef.current.context || buildVideoTrackingContext();
        if (!recordTime?.startTime) {
            setRecordTime({startTime: second});
            trackVideoAction({
                action: 'clip_mark_start',
                entityId: trackingContext?.entityId || null,
                entityLabel: trackingContext?.entityLabel || null,
                entityType: trackingContext?.entityType || 'video',
                metadata: {
                    ...(trackingContext?.metadata || {}),
                    clip_session_id: trackingStateRef.current.clipSessionId,
                    clip_start_second: Number(second.toFixed(3)),
                },
                sourceSurface: trackingContext?.sourceSurface || 'video',
            });
        } else if (recordTime?.startTime && recordTime.startTime < second) {
            setRecordTime((prev) => ({...prev, endTime: second}));
            !paused && togglePlay()
            trackVideoAction({
                action: 'clip_mark_complete',
                entityId: trackingContext?.entityId || null,
                entityLabel: trackingContext?.entityLabel || null,
                entityType: trackingContext?.entityType || 'video',
                metadata: {
                    ...(trackingContext?.metadata || {}),
                    clip_session_id: trackingStateRef.current.clipSessionId,
                    clip_end_second: Number(second.toFixed(3)),
                    clip_start_second: Number(recordTime.startTime.toFixed(3)),
                },
                sourceSurface: trackingContext?.sourceSurface || 'video',
            });
            //   setAddPlaylistOpenModal(true)
            // await handleSavePlaylist();
        }

    };

    useEffect(() => {
        console.log("Record time   ", recordTime);
    }, [recordTime]);

    const openDownload = (videoLink) => {
        const videoUrl = btoa(videoLink);
        const url = `${API_URL_V2}/video/download/${videoUrl}`;
        console.log('url', url);
        const trackingContext = trackingStateRef.current.context || buildVideoTrackingContext();
        trackVideoAction({
            action: 'download',
            entityId: trackingContext?.entityId || null,
            entityLabel: trackingContext?.entityLabel || null,
            entityType: trackingContext?.entityType || 'video',
            metadata: {
                ...(trackingContext?.metadata || {}),
                clip_session_id: trackingStateRef.current.clipSessionId,
                current_time: Number((videoRef.current?.currentTime || 0).toFixed(3)),
                download_mode: 'direct',
            },
            sourceSurface: trackingContext?.sourceSurface || 'video',
        });
        window.open(url, '_blank')
    }

    // test functionality for moving record markers
    // function startDragStart(e) {
    //     console.log('start drag start')
    //     setIsDraggingStart(true);
    // }

    // function startDragEnd(e) {
    //     console.log('start drag end')
    //     setIsDraggingEnd(true);
    // }

    // function drag(e) {
    //     if (!recordTime || (!isDraggingEnd && !isDraggingStart)) return;
    //     const rect = timelineContainer.current.getBoundingClientRect();
    //     if (isDraggingStart) {
    //         const newLeft = Math.min(Math.max(e.clientX - rect.left, 0), rect.width - startMarker.current?.offsetWidth || 0);
    //         const endLeft = parseInt(endMarker.current.style.left);
    //         console.log('can move', newLeft / rect.width < endLeft, newLeft, endLeft, newLeft / rect.width * 100)
    //         if (((newLeft / rect.width * 100) + 8 ) < endLeft) {
    //             const time = (newLeft / rect.width) * videoRef.current.duration;
    //             setRecordTime((prev) => ({ ...prev, startTime: time }));
    //             videoRef.current.currentTime = time;
    //         }
    //     }

    //     if (isDraggingEnd) {
    //         const newLeft = Math.min(Math.max(e.clientX - rect.left, 0), rect.width - endMarker.current?.offsetWidth);
    //         const startLeft = parseInt(startMarker.current.style.left);
    //         if (((newLeft / rect.width * 100) - 8) > startLeft) {
    //             const time = (newLeft / rect.width) * videoRef.current.duration;
    //             setRecordTime((prev) => ({ ...prev, endTime: time }));
    //             videoRef.current.currentTime = time;
    //         }
    //     }
    // }

    // function stopDrag() {
    //     setIsDraggingStart(false);
    //     setIsDraggingEnd(false);
    // }

    useImperativeHandle(ref, () => ({
        togglePlay,
        play: () => {
            videoRef.current?.play();
            setPaused(false);
        },
        pause: () => {
            videoRef.current?.pause();
            setPaused(true);
        },
        getCurrentTime: () => {
            return videoRef.current?.currentTime ?? 0;
        },
        seekTo: (second) => {
            if (!videoRef.current || typeof second !== 'number') return;
            try {
                videoRef.current.currentTime = Math.max(0, second);
            } catch (e) {
            }
        }
    }), [togglePlay]);

    // Only offer the sequence chip when this event actually belongs to a
    // possession sequence — without an id there is no sequence to expand.
    // On the tournament video page `record` is the marker itself; on the
    // playlist page the marker fields live under `record.details`.
    const sequenceId = record?.possession_sequence_id ?? record?.details?.possession_sequence_id;
    const hasPossessionSequence =
        sequenceId !== null && sequenceId !== undefined && sequenceId !== '';

    return (
        <>
            <div
                // onMouseMove={drag} 
                // onMouseUp={stopDrag}
                ref={videoContainer}
                style={{
                    ...styleWrapper, 
                    position: 'relative', 
                    lineHeight: 0, 
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div className={"realVideoContainer"}
                     data-tour="video-player"
                     style={{
                         aspectRatio: fullScreen ? 'unset' : '16/9',
                         height: fullScreen ? `calc(100% - ${controlsRef.current?.clientHeight || 119}px)` : 'auto',
                         width: '100%',
                         minHeight: fullScreen ? 'unset' : 'calc(30vw)',
                         maxHeight: fullScreen ? 'unset' : (window.location.pathname.includes('match/video') ? 'calc(100vh - 200px)' : 'calc(100vh - 120px)'),
                         position: 'relative',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         flexShrink: 0,
                         overflow: 'hidden',
                     }}
                >

                    <video
                        ref={videoRef}
                        src={videoLink}
                        id='video'
                        onEnded={() => {
                            // allow parent to flip isLoadingRef before deciding
                            setTimeout(() => {
                                const stillLoading = isLoadingRef?.current === true || loading;
                                if (!disabledNext && !stillLoading && !suppressAutoNextRef.current) {
                                    playNextVideo();
                                }
                            }, 50);
                        }}
                        autoPlay
                        // muted={timelineContainer.current > 0}
                        muted
                        playsInline
                        crossOrigin='anonymous'
                        style={{
                            width: '100%',
                            maxWidth: '100%',
                            height: '100%',
                            maxHeight: '100%',
                            minHeight: '100%',
                            borderRadius: '5px 5px 0 0',
                            background: '#04080287',
                            minWidth: stacked ? 'auto' : '879.49px',
                            objectFit: stacked ? 'cover' : 'contain',
                            display: 'block',
                            flexShrink: 0,
                        }}
                    />

                    {hasPossessionSequence && typeof onShowFullSequence === 'function' && (
                        sequenceChipHidden ? (
                            <SequenceChipHandle
                                type="button"
                                title="Sequence options"
                                onClick={() => setSequenceChipHidden(false)}
                            >
                                <CameraIcon />
                            </SequenceChipHandle>
                        ) : (
                            <FullSequenceChip data-tour="video-full-sequence">
                                {isFullSequenceActive && (
                                    <>
                                        <button
                                            type="button"
                                            className="chip-nav prev"
                                            title="Play the previous possession sequence"
                                            disabled={!hasPreviousSequence}
                                            onClick={() => onShowPreviousSequence?.()}
                                        >
                                            ‹ Show previous sequence
                                        </button>
                                        <span className="chip-divider" />
                                    </>
                                )}
                                <button
                                    type="button"
                                    className={isFullSequenceActive ? 'chip-action mid' : 'chip-action'}
                                    title={isFullSequenceActive
                                        ? 'Play only this event'
                                        : 'Play the whole possession sequence this event belongs to'}
                                    onClick={() => (isFullSequenceActive
                                        ? onShowEventOnly?.(record)
                                        : onShowFullSequence(record))}
                                >
                                    <CameraIcon />
                                    {isFullSequenceActive ? 'Show event only' : 'Show full sequence'}
                                </button>
                                {isFullSequenceActive && (
                                    <>
                                        <span className="chip-divider" />
                                        <button
                                            type="button"
                                            className="chip-nav next"
                                            title="Play the next possession sequence"
                                            disabled={!hasNextSequence}
                                            onClick={() => onShowNextSequence?.()}
                                        >
                                            Show next sequence ›
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="chip-close"
                                    aria-label="Hide"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSequenceChipHidden(true);
                                    }}
                                >
                                    <span className="chip-hide-label">Hide</span>
                                    ×
                                </button>
                            </FullSequenceChip>
                        )
                    )}

                </div>
                {loading && (
                    <Oval
                        height="80"
                        width="80"
                        color={localStorage.getItem("theme") === "light" ? "#2363F7" : "#CFDAE6"}
                        ariaLabel="oval-loading"
                        wrapperStyle={{
                            display: 'flex',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: '30%',
                            left: '45%',
                        }}
                        wrapperClass=""
                    />
                )}
                <VideoControlsWrapper
                    ref={controlsRef}
                    style={{opacity: videoLink ? 1 : 0.5, pointerEvents: videoLink ? 'auto' : 'none'}}
                >
                    <TimeLineWrapper>
                        <div className='timeline-container' ref={timelineContainer}>
                            <div className='timeline'>
                                <div className='thumb-indicator'></div>
                                {Boolean(recordTime?.startTime) && (
                                    <>
                                        <div
                                            className='record-point__timeline'
                                            style={{
                                                left: `${(recordTime.startTime / videoRef.current?.duration) * 100
                                                }%`,
                                                width: `calc(${(((recordTime.endTime ?? videoRef.current?.currentTime) -
                                                        recordTime.startTime) /
                                                    videoRef.current?.duration) *
                                                100
                                                }%)`,
                                            }}
                                        />
                                        <div
                                            className='record-point'
                                            ref={startMarker}
                                            // onMouseDown={startDragStart}
                                            style={{
                                                left: `${(recordTime.startTime / videoRef.current?.duration) * 100
                                                }%`,
                                            }}
                                        >
                                            <div className='record-point__time'>
                                                {formatDuration(recordTime.startTime)}
                                            </div>
                                            <RecordIcon width={35} height={35}/>
                                        </div>
                                    </>
                                )}
                                {Boolean(recordTime?.endTime) && (
                                    <div
                                        className='record-point'
                                        // onMouseDown={startDragEnd}
                                        ref={endMarker}
                                        style={{
                                            left: `${(recordTime.endTime / videoRef.current?.duration) * 100
                                            }%`,
                                        }}
                                    >
                                        <div className='record-point__time'>
                                            {formatDuration(recordTime.endTime)}
                                        </div>
                                        <RecordIcon width={35} height={35}/>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TimeLineWrapper>
                    <BottomControlsContainer>
                        <MainControlsContainer>
                            <LeftControls>
                                {!paused ? (
                                    <IconWrapper onClick={togglePlay}>
                                        <PlayIcon/>
                                    </IconWrapper>
                                ) : (
                                    <IconWrapper onClick={togglePlay}>
                                        <PauseIcon width={20} height={20}/>
                                    </IconWrapper>
                                )}
                                <IconWrapper onClick={() => handleChangeSecond(0 - skipTime)}>
                                    <Minus5Icon/>
                                </IconWrapper>
                                <input type="number" min="1" max="1000" value={skipTime}
                                       onChange={(e) => setSkipTime(e.target.value)}/>
                                <IconWrapper onClick={() => handleChangeSecond(skipTime)}>
                                    <Plus5Icon/>
                                </IconWrapper>
                                <ChangeVolume onVolumeChange={handleChangeVolume} volume={volume}/>
                            </LeftControls>
                            <CenterControls>
                                <IconWrapper
                                    style={{
                                        opacity: !disabledPrev ? 1 : 0.5,
                                        pointerEvents: !disabledPrev ? 'auto' : 'none',
                                    }}
                                    onClick={playPrevVideo}
                                >
                                    <PrevIcon/>
                                </IconWrapper>
                                <div ref={currentTimeElem} className='current-time'>
                                    0:00 / 0:00
                                </div>
                                <IconWrapper
                                    style={{
                                        opacity: !disabledNext ? 1 : 0.5,
                                        pointerEvents: !disabledNext ? 'auto' : 'none',
                                    }}
                                    onClick={playNextVideo}
                                >
                                    <NextIcon/>
                                </IconWrapper>
                            </CenterControls>

                            <RightControls>
                                <ChangeSpeed speed={playbackRate} onSpeedChange={handleChangeSpeed}/>
                                <VideoSettings changedOffsets={changedOffsets}/>
                                <IconWrapper onClick={toggleFullScreenMode}>
                                    <FullScreenIcon width={20}/>
                                </IconWrapper>
                            </RightControls>
                        </MainControlsContainer>
                    </BottomControlsContainer>
                    <Divider/>
                    <AdditionalActions>
                        <div onClick={handleScreenshot}>
                            <IconWrapper>
                                <ScreenShotIcon/>
                            </IconWrapper>
                            {l(1025)}
                        </div>
                        {isAuth && (
                            <>
                                <div
                                    style={{
                                        opacity: 1,
                                        pointerEvents: 'auto',
                                    }}
                                    onClick={() => handleRecord(videoRef.current?.currentTime ?? 0)}>
                                    <RecordIcon width={35} height={35}/>
                                    {recordTime?.startTime ? l(1109) : l(1026)}
                                </div>
                                <div
                                    style={{
                                        opacity: recordTime?.startTime && recordTime?.endTime ? 1 : 0.5,
                                        pointerEvents: recordTime?.startTime && recordTime?.endTime ? 'auto' : 'none',
                                    }}
                                    onClick={addRecordToPlaylist}
                                >
                                    {l(1027)}
                                </div>
                                <div
                                    onClick={() => setRecordTime({startTime: 0, endTime: 0})}
                                    style={{
                                        opacity: recordTime?.startTime || recordTime?.endTime ? 1 : 0.5,
                                        pointerEvents: recordTime?.startTime || recordTime?.endTime ? 'auto' : 'none',
                                    }}
                                >
                                    {l(1028)}
                                </div>
                            </>
                        )}
                        {/* add needDownload prop if need hide this button for special pages  */}
                        {videoLink && (
                            <div
                                onClick={() => {
                                    openDownload(videoLink)
                                    addNotification('Download started.', 'info')
                                }}
                            >
                                {l(1029)}
                            </div>
                        )}

                        {videoLink && showAddToPlaylistButton && (
                            <div
                                onClick={() => {
                                    addToPlaylist(videoLink)
                                }}
                            >
                                {l(426)}
                            </div>
                        )}
                    </AdditionalActions>
                </VideoControlsWrapper>
            </div>
            {/* display notifications */}
            <NotificationList
                notifications={notifications}
                onClose={removeNotification}
            />
        </>

    )
})
